import { localizedStrings } from '@core/strings'
import React, { useEffect } from 'react'
import { Flex, Text } from '@fundamentals'
import { useDeepCompareMemo } from 'use-deep-compare'
import {
  CheckCircleOutlined,
  InfoOutlined,
  TimelapseOutlined,
} from '@mui/icons-material'
import { Chip, Tooltip } from '@common/material'
import { ViewApprovalsPermitModal } from '@modules/permitDetails/modals/ViewApprovalsPermitModal'
import { ViewChangesDone } from '@modules/permitDetails/modals/ViewChangesDone'
import { ReasonForRejectionPermitModal } from '@modules/permitDetails/modals/ReasonForRejectionPermitModal'
import { ReasonForCancelPermitModal } from '@modules/permitDetails/modals/ReasonForCancelPermitModal'
import { useModal } from '@common/GlobalModal'
import { useRouter } from 'next/router'
import { usePermitTasks } from '@core/react-query/features/permits/permit'
import { PermitTask } from '@core/entities/task/PermitTask'
import { usePermitChips } from '@modules/permitDetails/usePermitChips'
import { Permit } from '@core/entities/permit/Permit'
import { ViewFinalSignOffsPermitModal } from '../modals/ViewFinalSignOffsPermitModal'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { ObjectDetectionRobotChip } from '@elements/ObjectDetection/ObjectDetectionRobotChip'
import { PermitTabModal } from '@modules/permitDetails/tabs/PermitTabModal'
import { PeopleOutlined } from '@mui/icons-material'
import useFeatureFlags from '@core/providers/useFeatureFlags'

type ChipsProps = {
  permit: Permit
}

const Chips: React.FC<ChipsProps> = ({ permit }) => {
  const router = useRouter()
  const { showModal } = useModal()
  const { flags } = useFeatureFlags(['web_permit_check_in'])

  const { data: tasks, refetch } = usePermitTasks(
    { permitId: permit.id },
    {
      enabled: !!permit.id,
    },
  )

  const { primaryChip } = usePermitChips()

  const allApprovals = permit?.approvalReviews?.approvals

  const awaitingApprovalsText = permit?.getApprovalProgressText()

  const chips = useDeepCompareMemo(() => {
    const chips = []
    chips.push(
      primaryChip(permit, {
        dataTest: 'permit-status-chip',
      }),
    )

    // Child permit approved but held in Pre Start until its parent permit
    // opens — surface the dependency next to the primary status.
    if (permit?.isAwaitingParentOpen()) {
      chips.push(
        <Chip
          key='awaiting-parent-permit'
          icon={<TimelapseOutlined />}
          variant='outlined'
          color='warning'
          label={localizedStrings.awaitingParentPermit}
        />,
      )
    }

    if (flags.web_permit_check_in && permit.isCheckInEnabled()) {
      const { onSite, overdue, total } = permit.getOnSiteSummary()
      chips.push(
        <Chip
          key='on-site-summary'
          icon={<PeopleOutlined />}
          variant='outlined'
          color={overdue ? 'error' : onSite ? 'success' : 'default'}
          label={localizedStrings.onSiteSummary(onSite, total)}
          data-test='on-site-summary-chip'
        />,
      )
    }

    // Number of approvals pill when permit is awaiting approval
    if (awaitingApprovalsText) {
      const allReviews = allApprovals?.length
      if (allReviews > 1) {
        const tooltipContent = allApprovals?.map((a, index) => {
          const Icon = () =>
            a?.action === 'APPROVED' ? (
              <CheckCircleOutlined sx={{ pr: 0.5 }} />
            ) : (
              <TimelapseOutlined sx={{ pr: 0.5 }} />
            )
          const label =
            a?.action === 'APPROVED'
              ? `${a?.submittedBy?.firstName} ${a?.submittedBy?.lastName} (${a?.approval?.label})`
              : a?.approval?.label

          return (
            <Flex flexDirection='column' key={index}>
              <Flex alignItems='center'>
                <Icon />
                {label}
              </Flex>
            </Flex>
          )
        })

        chips.push(
          <Tooltip title={tooltipContent} arrow>
            <Chip
              variant='outlined'
              sx={{ border: 'none' }}
              icon={<InfoOutlined />}
              color={'info'}
              label={
                <Text color='primary.main' sx={{ textDecoration: 'underline' }}>
                  {awaitingApprovalsText}
                </Text>
              }
              onClick={() => {
                showModal(<ViewApprovalsPermitModal approvals={allApprovals} />)
              }}
            />
          </Tooltip>,
        )
      }
    }

    // Number of finalSignOffs pill when permit is awaiting finalSignOff
    if (
      permit.status === 'AWAITING_FINAL_SIGN_OFF' &&
      permit?.finalSignOffReviews?.reviews?.length > 0
    ) {
      const doneReviewsIndex = permit.finalSignOffReviews.getDoneReviewsIndex()

      const allReviews = permit.finalSignOffReviews?.reviews?.length

      if (allReviews > 0) {
        const tooltipContent = permit?.finalSignOffReviews?.reviews?.map(
          ({ finalSignOff, createdBy }, index) => {
            const Icon = () =>
              index < doneReviewsIndex ? (
                <CheckCircleOutlined sx={{ pr: 0.5 }} />
              ) : (
                <TimelapseOutlined sx={{ pr: 0.5 }} />
              )
            const label =
              index < doneReviewsIndex
                ? `${createdBy?.getFullName()} (${finalSignOff?.label})`
                : finalSignOff?.label

            return (
              <Flex flexDirection='column' key={index}>
                <Flex alignItems='center'>
                  <Icon />
                  {label}
                </Flex>
              </Flex>
            )
          },
        )

        chips.push(
          <Tooltip title={tooltipContent} arrow>
            <Chip
              variant='outlined'
              sx={{ border: 'none' }}
              icon={<InfoOutlined />}
              color={'info'}
              label={
                <Text color='primary.main' sx={{ textDecoration: 'underline' }}>
                  {`${doneReviewsIndex} out of ${allReviews} - View Final Sign Offs`}
                </Text>
              }
              onClick={() => {
                showModal(
                  <ViewFinalSignOffsPermitModal
                    finalSignOffReviews={permit.finalSignOffReviews}
                  />,
                )
              }}
            />
          </Tooltip>,
        )
      }
    }

    // Requested changes
    if (
      permit.status === 'REQUESTED' &&
      !!permit?.lastRequestedChange?.addressedBy
    ) {
      // Requested changes message
      chips.push(
        <Tooltip title={permit.lastRequestedChange.comment}>
          <Chip
            variant='outlined'
            sx={{ border: 'none' }}
            icon={<InfoOutlined />}
            color={'info'}
            label={
              <Text
                data-test='changes-chip-text'
                color='primary.main'
                sx={{ textDecoration: 'underline' }}
              >
                {localizedStrings.changesDone}
              </Text>
            }
            onClick={() => showModal(<ViewChangesDone permit={permit} />)}
          />
        </Tooltip>,
      )
    }

    // Reject reason
    if (permit.status === 'REJECTED') {
      chips.push(
        <Tooltip title={permit?.rejectReason}>
          <Chip
            variant='outlined'
            sx={{ border: 'none' }}
            icon={<InfoOutlined />}
            color='info'
            label={
              <Text color='primary.main' sx={{ textDecoration: 'underline' }}>
                {localizedStrings.viewReason}
              </Text>
            }
            onClick={() => {
              showModal(<ReasonForRejectionPermitModal permit={permit} />)
            }}
          />
        </Tooltip>,
      )
    }

    if (permit.status === 'EXTENSION_REQUESTED') {
      chips.push(
        <Chip
          icon={<InfoOutlined />}
          variant='outlined'
          color={Permit.getStatusChipColor(permit.status)}
          label={localizedStrings.extensionRequested}
        />,
      )
    }

    // Cancel reason
    if (permit.status === 'CANCELLED') {
      chips.push(
        <Tooltip title={permit?.cancelReason}>
          <Chip
            variant='outlined'
            sx={{ border: 'none' }}
            icon={<InfoOutlined />}
            color='info'
            label={
              <Text color='primary.main' sx={{ textDecoration: 'underline' }}>
                {localizedStrings.viewReason}
              </Text>
            }
            onClick={() => {
              showModal(<ReasonForCancelPermitModal permit={permit} />)
            }}
          />
        </Tooltip>,
      )
    }

    if (permit?.warnings?.length) {
      chips.push(
        <Flex
          alignItems='center'
          sx={{ mx: 1, cursor: 'pointer' }}
          onClick={() => {
            showModal(<PermitTabModal permit={permit} onlyShowWarnings />)
          }}
        >
          <WarningAmberOutlinedIcon color='warning' />
          <Text
            variant='body2'
            color='primary.main'
            sx={{ ml: 0.5, fontWeight: 500, textDecoration: 'underline' }}
          >{`${permit?.warnings?.length} Warning${
            permit?.warnings?.length > 1 ? 's' : ''
          }`}</Text>
        </Flex>,
      )
    }

    const numberOfOutstandingTasks = PermitTask.getNumberOfTasksByStatus(
      tasks || [],
      'TO_DO',
    )
    if (
      numberOfOutstandingTasks > 0 &&
      !['SUSPENDED', 'SUSPENDED_RESUME_REQUESTED'].includes(permit.status)
    ) {
      chips.push(
        <Chip
          icon={<InfoOutlined />}
          color='info'
          label={`${numberOfOutstandingTasks} Task${
            numberOfOutstandingTasks > 1 ? 's' : ''
          } Outstanding`}
          onClick={() => {
            router.query.tab = 'Tasks'
            router.push(router, undefined, { shallow: true })
          }}
        />,
      )
    }

    return chips
  }, [permit, tasks, flags.web_permit_check_in])

  useEffect(() => {
    // re-fetch when permit is approved and status is updated
    if (permit?.status === 'ISSUED' && tasks?.length === 0) {
      refetch()
    }
  }, [permit, tasks, refetch])

  return (
    <Flex gap={1} alignItems={'center'}>
      {chips}
      <ObjectDetectionRobotChip permit={permit} />
    </Flex>
  )
}

export default Chips
